import { useQuery } from '@tanstack/react-query';
import queryKeys from './queryKeys';
import {
  getChatroomApi,
  loadMoreMessagesApi,
  getAuthToken,
} from '../services/assistantChatService.jsx';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Fetch or retrieve cached chatroom for the active user.
 * Cached for 24 hours / all day until logout with persistent storage.
 */
export function useAssistantChatroom(userInfo, options = {}) {
  const userId = userInfo?.users?.id;
  const userRole = userInfo?.users?.type;

  return useQuery({
    queryKey: queryKeys.assistantChat.chatroom(userRole, userId),
    queryFn: async () => {
      const response = await getChatroomApi();
      if (!response?.data?.success) {
        return null;
      }
      if (userRole === 'Nurse') {
        const list = response.data.data;
        return Array.isArray(list) && list.length > 0 ? list[0] : null;
      }
      return response.data.data;
    },
    enabled: Boolean(userId) && Boolean(getAuthToken()),
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
    meta: { persist: true },
    ...options,
  });
}

/**
 * Fetch initial assistant chat messages.
 * Cached for 24 hours / all day until logout with persistent storage.
 * Does not refetch unless explicitly prompted; incoming/outgoing messages are pushed directly to cache.
 */
export function useAssistantMessages(chatroomId, options = {}) {
  return useQuery({
    queryKey: queryKeys.assistantChat.messages(chatroomId),
    queryFn: async () => {
      const response = await loadMoreMessagesApi(chatroomId);
      if (response?.data?.success && response.data.data) {
        const { messages = [], has_more = false, oldest_message_id = null } = response.data.data;
        return {
          messages: messages.slice().reverse(), // Chronological order (oldest first)
          hasMore: Boolean(has_more),
          oldestMessageId: oldest_message_id,
        };
      }
      return {
        messages: [],
        hasMore: false,
        oldestMessageId: null,
      };
    },
    enabled: Boolean(chatroomId) && Boolean(getAuthToken()),
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
    meta: { persist: true },
    ...options,
  });
}

/**
 * Push an incoming or outgoing message into the persisted TanStack Query cache in-place without refetching.
 */
export function addMessageToAssistantCache(queryClient, chatroomId, newMessage) {
  if (!queryClient || !chatroomId || !newMessage) return;
  const key = queryKeys.assistantChat.messages(chatroomId);

  queryClient.setQueryData(key, (oldData) => {
    if (!oldData) {
      return {
        messages: [newMessage],
        hasMore: false,
        oldestMessageId: newMessage.id,
      };
    }

    const currentMessages = oldData.messages || [];
    const alreadyExists = currentMessages.some((msg) => msg.id === newMessage.id);
    if (alreadyExists) {
      return oldData;
    }

    return {
      ...oldData,
      messages: [...currentMessages, newMessage],
    };
  });
}

/**
 * Prepend older paginated messages to the persisted TanStack Query cache.
 */
export function prependMessagesToAssistantCache(
  queryClient,
  chatroomId,
  olderChronologicalMessages,
  hasMore,
  oldestMessageId
) {
  if (!queryClient || !chatroomId) return;
  const key = queryKeys.assistantChat.messages(chatroomId);

  queryClient.setQueryData(key, (oldData) => {
    const currentMessages = oldData?.messages || [];
    const existingIds = new Set(currentMessages.map((m) => m.id));
    const dedupedOlder = olderChronologicalMessages.filter((m) => !existingIds.has(m.id));

    return {
      messages: [...dedupedOlder, ...currentMessages],
      hasMore: hasMore !== undefined ? hasMore : oldData?.hasMore ?? false,
      oldestMessageId: oldestMessageId !== undefined ? oldestMessageId : oldData?.oldestMessageId ?? null,
    };
  });
}

/**
 * Imperatively fetch or retrieve cached assistant chatroom
 */
export async function fetchAssistantChatroom(queryClient, userInfo) {
  const userId = userInfo?.users?.id;
  const userRole = userInfo?.users?.type;
  const key = queryKeys.assistantChat.chatroom(userRole, userId);

  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await getChatroomApi();
      if (!response?.data?.success) return null;
      if (userRole === 'Nurse') {
        const list = response.data.data;
        return Array.isArray(list) && list.length > 0 ? list[0] : null;
      }
      return response.data.data;
    },
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
    meta: { persist: true },
  });
}

/**
 * Imperatively fetch or retrieve cached assistant messages
 */
export async function fetchAssistantMessages(queryClient, chatroomId) {
  const key = queryKeys.assistantChat.messages(chatroomId);

  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await loadMoreMessagesApi(chatroomId);
      if (response?.data?.success && response.data.data) {
        const { messages = [], has_more = false, oldest_message_id = null } = response.data.data;
        return {
          messages: messages.slice().reverse(),
          hasMore: Boolean(has_more),
          oldestMessageId: oldest_message_id,
        };
      }
      return {
        messages: [],
        hasMore: false,
        oldestMessageId: null,
      };
    },
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
    meta: { persist: true },
  });
}
